/**
 * ChartZoomBar — status line for a zoomable chart.
 *
 * Purpose: tells the user how to zoom while the chart shows everything, and
 *   which x range is showing (plus a Reset button) while zoomed.
 * Props:
 *   - zoom: the object returned by useChartZoom ({ range, isZoomed, reset })
 *   - formatValue: (xValue:number) => string — turns an x-scale value into a
 *     label (a date, "Day 12", …). Defaults to the rounded number.
 *   - className: optional extra class
 *   - showRange: boolean (default true) — false keeps the gesture hint even
 *     while zoomed, for pages whose own controls already show the window
 *     (e.g. From/To date pickers driven by the zoom)
 * Behaviour:
 *   - The gesture hint is hidden on touch-only devices (no hover), where the
 *     mouse gestures don't exist.
 *   - Render one bar per zoom controller: charts that share a controller
 *     (e.g. stacked panels on one date axis) share one bar.
 */
import React from "react";
import Button from "../FormElements/Button";
import "./styling/ChartZoomBar.css";

const defaultFormat = (v) => Math.round(v).toLocaleString();

const ChartZoomBar = ({ zoom, formatValue = defaultFormat, className = "", showRange = true }) => {
  if (!zoom) return null;
  return (
    <div className={`chart-zoom-bar ${className}`.trim()}>
      {zoom.isZoomed && showRange ? (
        <>
          <span className="chart-zoom-bar__range">
            Showing {formatValue(zoom.range.min)} – {formatValue(zoom.range.max)}
          </span>
          <Button size="small" inverse onClick={zoom.reset}>Reset zoom</Button>
        </>
      ) : (
        <span className="chart-zoom-bar__hint">
          Drag or Shift + scroll to zoom · Shift + drag to pan · Double-click to reset
        </span>
      )}
    </div>
  );
};

export default ChartZoomBar;
